import { useAuthStore } from "./auth";

const RETURN_TO_KEY = "booqly-app.return-to";

/**
 * On bootstrap, hydrate the auth store from a `#token=...&email=...` hash that
 * booqly-auth appends after a successful sign-in. We then strip the fragment
 * so the credential isn't visible in the URL or copy-pasted by mistake.
 *
 * If the user landed here via the session-expired bouncer
 * (`bounceToSignIn` in `./api.ts` writes `localStorage[booqly-app.return-to]`
 * before redirecting to the auth subapp), this also reads + clears that key
 * and `window.location.replace`s to the saved path so the operator picks up
 * exactly where they left off.
 *
 * This is a dev-friendly handoff between the two surfaces. Production runs
 * everything under `*.booqly.co` with a shared cookie domain, so a refresh-token
 * cookie is automatically available and this fallback is unused.
 */
export function bootstrapTokenFromHash(): void {
  if (typeof window === "undefined") return;
  const hash = window.location.hash;
  if (!hash || hash.length < 2) return;
  const params = new URLSearchParams(hash.slice(1));
  const token = params.get("token");
  const email = params.get("email");
  if (!token) return;

  useAuthStore.getState().setTokens(token, email ?? "");
  history.replaceState(null, "", window.location.pathname + window.location.search);

  // Replay the navigation if the user was bounced from a deep page. Path is
  // strictly validated to be a same-origin route (must start with `/` and
  // never `//` — closes the open-redirect surface).
  try {
    const back = localStorage.getItem(RETURN_TO_KEY);
    if (back) {
      localStorage.removeItem(RETURN_TO_KEY);
      const safe =
        back.startsWith("/") &&
        !back.startsWith("//") &&
        back !== window.location.pathname + window.location.search;
      if (safe) window.location.replace(back);
    }
  } catch {
    // localStorage may be disabled — non-fatal, user just lands on `/`.
  }
}
